import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { identity, isApiError } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Title, Button, Group, Text, Badge } from '@repletoryreach/sdk-ui';
import { DataTable, Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Menu } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Checkbox, Select, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';

/**
 * Teams (7 October 2026): named groups of the organisation's people, such as
 * Sales or Support, each with an optional lead. Every member sees them; owners
 * and admins change them, and the service refuses anyone else.
 */

const MUTED = 'var(--rr-color-text-muted)';
const NAME_MAX = 80;
const DESCRIPTION_MAX = 500;

type Person = identity.User;

function personName(p: Person | undefined): string {
  return p ? (p.display_name?.trim() || p.email || '') : '';
}

export function TeamsCard() {
  const t = useT();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const canManage = me?.role == null || identity.isOrgAdmin(me.role);

  const teamsQ = useQuery({ queryKey: ['teams'], queryFn: () => identity.listTeams() });
  // Everyone in the organisation, for the lead and member pickers.
  const peopleQ = useQuery({ queryKey: ['team', 'all-people'], queryFn: () => identity.listUsers({ limit: 500 }) });
  const people = useMemo(() => (peopleQ.data?.data ?? []) as Person[], [peopleQ.data]);
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);

  const [editing, setEditing] = useState<identity.Team | 'new' | null>(null);
  const [membersOf, setMembersOf] = useState<identity.Team | null>(null);

  const rows = (teamsQ.data?.data ?? []) as Indexed<identity.Team>[];

  const remove = useMutation({
    mutationFn: (team: identity.Team) => identity.deleteTeam(team.id),
    onSuccess: (_r, team) => {
      qc.invalidateQueries({ queryKey: ['teams'] });
      notifySuccess(t('settings.teams.toast.deleted', { name: team.name }), t('settings.teams.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.teams.title'))
  });

  function confirmDelete(team: identity.Team) {
    openConfirmModal({
      title: t('settings.teams.modal.delete.title'),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('settings.teams.modal.delete.body', { name: team.name })}</Text>
          <Text role="caption" c={MUTED}>{t('settings.teams.modal.delete.keeps')}</Text>
        </Stack>
      ),
      labels: { confirm: t('settings.teams.actions.delete'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(team)
    });
  }

  const createButton = canManage
    ? <Button size="sm" onClick={() => setEditing('new')}>{t('settings.teams.actions.create')}</Button>
    : undefined;

  return (
    <Card withBorder data-testid="teams-card">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Stack gap={2}>
            <Title order={4}>{t('settings.teams.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('settings.teams.intro')}</Text>
          </Stack>
          {rows.length > 0 && createButton}
        </Group>

        {teamsQ.error && (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{t('settings.teams.loadError')}</span>
              <Button size="xs" variant="default" onClick={() => void teamsQ.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}

        {!teamsQ.isPending && !teamsQ.error && rows.length === 0 ? (
          <EmptyState
            title={t('settings.teams.empty')}
            description={canManage ? t('settings.teams.empty.hint') : t('settings.teams.empty.hintReadOnly')}
            action={createButton}
          />
        ) : (
          <DataTable<Indexed<identity.Team>>
            records={rows}
            loading={teamsQ.isPending}
            idAccessor="id"
            emptyState={t('settings.teams.empty')}
            columns={[
              {
                accessor: 'name',
                title: t('common.name'),
                render: (team) => (
                  <Stack gap={2}>
                    <Group gap={6} wrap="nowrap">
                      <Badge size="xs" variant="dot" color={team.colour ?? 'gray'}>{team.name}</Badge>
                    </Group>
                    {team.description && <Text role="caption" c={MUTED}>{team.description}</Text>}
                  </Stack>
                )
              },
              {
                accessor: 'lead_user_id',
                title: t('settings.teams.lead'),
                render: (team) => team.lead_user_id && byId.get(team.lead_user_id)
                  ? <span>{personName(byId.get(team.lead_user_id))}</span>
                  : <Text role="caption" c={MUTED}>{t('settings.teams.noLead')}</Text>
              },
              {
                accessor: 'member_count',
                title: t('settings.teams.members'),
                render: (team) => {
                  const names = team.member_user_ids.map((id) => personName(byId.get(id))).filter(Boolean);
                  return (
                    <Stack gap={2}>
                      <span>{t('settings.teams.memberCount', { count: team.member_count })}</span>
                      {names.length > 0 && (
                        <Text role="caption" c={MUTED}>
                          {names.length > 3
                            ? t('settings.teams.memberPreviewMore', { names: names.slice(0, 3).join(', '), more: names.length - 3 })
                            : names.join(', ')}
                        </Text>
                      )}
                    </Stack>
                  );
                }
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                render: (team) => !canManage ? <></> : (
                  <Menu>
                    <Menu.Target>
                      <Button size="xs" variant="subtle" aria-label={t('settings.teams.actions.for', { name: team.name })}>⋯</Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => setEditing(team)}>{t('settings.teams.actions.edit')}</Menu.Item>
                      <Menu.Item onClick={() => setMembersOf(team)}>{t('settings.teams.actions.members')}</Menu.Item>
                      <Menu.Divider />
                      <Menu.Item color="red" onClick={() => confirmDelete(team)}>{t('settings.teams.actions.delete')}</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )
              }
            ]}
          />
        )}
      </Stack>

      <TeamDrawer
        team={editing}
        people={people}
        onClose={() => setEditing(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['teams'] })}
      />
      <MembersDrawer
        team={membersOf}
        people={people}
        onClose={() => setMembersOf(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['teams'] })}
      />
    </Card>
  );
}

function TeamDrawer({
  team,
  people,
  onClose,
  onSaved
}: {
  team: identity.Team | 'new' | null;
  people: Person[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const isNew = team === 'new';
  const existing = team && team !== 'new' ? team : null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [lead, setLead] = useState('');
  const [colour, setColour] = useState<identity.TeamColour>('blue');
  const [nameError, setNameError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!team) return;
    setName(existing?.name ?? '');
    setDescription(existing?.description ?? '');
    setLead(existing?.lead_user_id ?? '');
    setColour(existing?.colour ?? 'blue');
    setNameError('');
  }, [team]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setNameError(t('settings.teams.nameRequired')); return; }
    setBusy(true);
    try {
      const body = { name: trimmed, description: description.trim() || null, lead_user_id: lead || null, colour };
      if (existing) await identity.updateTeam(existing.id, body);
      else await identity.createTeam(body);
      notifySuccess(t(isNew ? 'settings.teams.toast.created' : 'settings.teams.toast.saved', { name: trimmed }), t('settings.teams.title'));
      onSaved();
      onClose();
    } catch (err) {
      if (isApiError(err) && err.code === 'TEAM_NAME_TAKEN') setNameError(t('settings.teams.nameTaken'));
      else notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('settings.teams.title'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      opened={!!team}
      onClose={onClose}
      title={isNew ? t('settings.teams.actions.create') : t('settings.teams.drawer.editTitle', { name: existing?.name ?? '' })}
      position="right"
      size="md"
    >
      <form onSubmit={submit}>
        <Stack gap="md" style={{ padding: 4 }}>
          <TextInput
            label={t('common.name')}
            description={t('settings.teams.nameHint')}
            error={nameError}
            value={name}
            maxLength={NAME_MAX}
            onChange={(e) => { setName(e.currentTarget.value); setNameError(''); }}
            required
            autoFocus
          />
          <Textarea
            label={t('common.description')}
            description={t('settings.teams.descriptionHint')}
            value={description}
            maxLength={DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          <Select
            label={t('settings.teams.lead')}
            description={t('settings.teams.leadHint')}
            value={lead}
            onChange={(e) => setLead(e.currentTarget.value)}
            data={[
              { value: '', label: t('settings.teams.noLead') },
              ...people.map((p) => ({ value: p.id, label: personName(p) }))
            ]}
          />
          <Stack gap="xs">
            <Select
              label={t('settings.teams.colour')}
              description={t('settings.teams.colourHint')}
              value={colour}
              onChange={(e) => setColour(e.currentTarget.value as identity.TeamColour)}
              data={identity.TEAM_COLOURS.map((c) => ({ value: c, label: t(`settings.teams.colour.${c}`) }))}
            />
            <div>
              <Badge size="sm" variant="dot" color={colour}>{name.trim() || t('settings.teams.previewName')}</Badge>
            </div>
          </Stack>

          <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={busy}>{isNew ? t('settings.teams.actions.create') : t('action.saveChanges')}</Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function MembersDrawer({
  team,
  people,
  onClose,
  onSaved
}: {
  team: identity.Team | null;
  people: Person[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!team) return;
    setChosen(new Set(team.member_user_ids));
    setQuery('');
  }, [team]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? people.filter((p) => (p.email ?? '').toLowerCase().includes(q) || (p.display_name ?? '').toLowerCase().includes(q))
      : people;
  }, [people, query]);

  function toggle(id: string, on: boolean) {
    setChosen((s) => {
      const next = new Set(s);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  const leadDropped = !!team?.lead_user_id && !chosen.has(team.lead_user_id);

  async function save() {
    if (!team) return;
    setBusy(true);
    try {
      await identity.setTeamMembers(team.id, [...chosen]);
      notifySuccess(t('settings.teams.toast.membersSaved', { name: team.name }), t('settings.teams.title'));
      onSaved();
      onClose();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('settings.teams.title'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      opened={!!team}
      onClose={onClose}
      title={t('settings.teams.drawer.membersTitle', { name: team?.name ?? '' })}
      position="right"
      size="md"
    >
      <Stack gap="md" style={{ padding: 4 }}>
        <Text role="bodySmall" c={MUTED}>{t('settings.teams.drawer.membersHint')}</Text>
        <TextInput
          type="search"
          aria-label={t('settings.team.search')}
          placeholder={t('settings.team.search.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
        <Text role="caption" c={MUTED}>{t('settings.teams.selected', { count: chosen.size })}</Text>
        <Stack gap="xs" data-testid="team-members-list">
          {shown.length === 0 && <Text role="bodySmall" c={MUTED}>{t('settings.team.emptySearch')}</Text>}
          {shown.map((p) => (
            <Checkbox
              key={p.id}
              label={p.display_name?.trim() ? `${p.display_name} (${p.email ?? ''})` : (p.email ?? '')}
              checked={chosen.has(p.id)}
              onChange={(on) => toggle(p.id, on)}
            />
          ))}
        </Stack>
        {leadDropped && <Alert color="warning">{t('settings.teams.leadDropped')}</Alert>}

        <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={busy} onClick={() => void save()}>{t('action.saveChanges')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
